import React, {
    Fragment,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react"
import { createPortal } from "react-dom"
import type { QuotationStatusVisualVariant } from "../quotation/quotationStatusVariant"
import styles from "./quotationStatusPill.module.scss"
import IconsColor from "../../util/iconsColor";
import {useQuotation} from "../../context/quotationContext";
import {
    DROPDOWN_MIN_WIDTH,
    DropdownPlacement,
    getFlagStatusChangeableValue,
    getMarkQuotationStatusConfirmVariant,
    getStatusOptionsToShow,
    PAGE_SCROLL_KEYS,
    resolveDropdownPlacement,
} from "./quotationStatusPillUtil";
import {IQuotationStatus} from "../../models";
import {agent} from "../../apiClient/api";
import {useSnackbar} from "../../context/snackbarContext";
import MarkQuotationStatusConfirmPopup, {
    type MarkQuotationStatusConfirmVariant,
} from "../quotation/MarkQuotationStatusConfirmPopup";
import { buildQuotationStatusChangePayload } from "../quotation/quotationStatusChangeUtil";
import { LuFileClock } from "react-icons/lu";
import { IoMdInformationCircleOutline } from "react-icons/io";
import ActivityHistoryPopup from "./ActivityHistoryPopup";
import type { IFollowUpItem } from "../quotationList/quotationListTypes";
import type { QuotationDownloadItemModel } from "../../models/axiosModel";
import { canShowAllFollowUpTypeOptions } from "./activityHistoryUtil";
import {
    buildQuotationListStatusMetaView,
    type QuotationListStatusMetaInput,
} from "../quotationList/quotationListStatusMetaUtil";


const variantClass: Record<QuotationStatusVisualVariant, string> = {
    draft: styles.pillDraft,
    "ready to send": styles.pillReadyToSend,
    sent: styles.pillSent,
    "follow up": styles.pillFollowUp,
    confirmed: styles.pillConfirmed,
    lost: styles.pillLost,
    default: styles.pillDefault,
}

type IQuotationStatusPillProps = {
    status_name: string
    status_id: number
    variant: QuotationStatusVisualVariant
    className?: string
    quotation_id?: string;
    sta_code?: string;
    started_at?: string | null;
    created_at?: string | null;
    quotation_downloads?: QuotationDownloadItemModel[];
    follow_ups?: IFollowUpItem[];
    flagViewOnly?: boolean;
    flagShowActivityHistory?: boolean;
    flagShowListStatusMeta?: boolean;
    listStatusMeta?: Omit<QuotationListStatusMetaInput, "variant">;
    onActivityHistoryOpen?: () => void;
    onFollowUpsChange?: (quotationId: string, followUps: IFollowUpItem[]) => void;
    flagShowStatusLabel?: boolean;
    dropdownOptionClassName?: string;
    flagMatchAnchorWidth?: boolean;
}

const QuotationStatusPill: React.FC<IQuotationStatusPillProps> = (props) => {

    const {
        status_id,
        status_name,
        variant,
        className,
        quotation_id,
        sta_code,
        started_at = null,
        created_at = null,
        quotation_downloads = [],
        follow_ups = [],
        flagViewOnly,
        flagShowActivityHistory,
        flagShowListStatusMeta,
        listStatusMeta,
        onActivityHistoryOpen,
        onFollowUpsChange,
        flagShowStatusLabel,
        dropdownOptionClassName,
        flagMatchAnchorWidth,
    } = props;

    const [isOpen, setIsOpen] = useState(false)
    const [isLostReasonPopoverOpen, setIsLostReasonPopoverOpen] = useState(false)
    const [lostReasonPopoverPosition, setLostReasonPopoverPosition] = useState<{
        top: number
        left: number
    } | null>(null)
    const [menuPosition, setMenuPosition] = useState<{
        top: number
        left: number
        placement: DropdownPlacement
        isReady: boolean
    } | null>(null)
    const [flagShowActivityHistoryPopup, setFlagShowActivityHistoryPopup] = useState(false);
    const [isHoveringLostReason, setIsHoveringLostReason] = useState(false)
    const [statusConfirmVariant, setStatusConfirmVariant] = useState<MarkQuotationStatusConfirmVariant | null>(null)
    const [pendingStatusOption, setPendingStatusOption] = useState<IQuotationStatus | null>(null)
    const [isStatusChangeSubmitting, setIsStatusChangeSubmitting] = useState(false)

    const anchorRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const lostReasonInfoRef = useRef<HTMLButtonElement>(null)
    const lostReasonPopoverRef = useRef<HTMLDivElement>(null)

    const {
        quotationStatusOptions,
        quotationId,
        quotationFollowUpTypeOptions,
        setQuotationData,
        setCurrentQuotationStatusObj
    } = useQuotation();
    const {
        showError,
        showSuccess,
    } = useSnackbar();

    const handleOptionClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, option: IQuotationStatus) => {
        event.stopPropagation();
        setIsOpen(false)

        const confirmVariant = getMarkQuotationStatusConfirmVariant(option)
        if (confirmVariant) {
            setPendingStatusOption(option)
            setStatusConfirmVariant(confirmVariant)
            return
        }

        void changeQuotationStatusApi(option)
    }

    const changeQuotationStatusApi = async (
        option: IQuotationStatus,
        remark?: string,
    ) => {
        const activeQuotationId = quotation_id ?? quotationId
        if (!activeQuotationId) {
            showError("Something went wrong, Please try again!")
            return
        }

        setIsStatusChangeSubmitting(true)
        try {
            const response = await agent.QuotationChangeStatusAPI.put(
                buildQuotationStatusChangePayload(
                    activeQuotationId,
                    option.quotation_status_id,
                    remark,
                ),
                false,
            )
            if (!response.status) {
                throw new Error("Something went wrong")
            }
            const getQuotationDetailsApiResponse = await agent.QuotationDetailsAPI.get(activeQuotationId, true)
            if (getQuotationDetailsApiResponse.status) {
                setQuotationData(getQuotationDetailsApiResponse)
                const quotationDetail = getQuotationDetailsApiResponse.data?.quotation;
                if (quotationDetail) {
                    setCurrentQuotationStatusObj({
                        quotation_status_id: quotationDetail.quotation_status_id,
                        quotation_status_name: quotationDetail.quotation_status_name,
                    })
                }
            }

            const confirmVariant = getMarkQuotationStatusConfirmVariant(option)
            if (confirmVariant === "confirmed") {
                showSuccess("Quotation marked as confirmed")
            } else if (confirmVariant === "lost") {
                showSuccess("Quotation marked as lost")
            }

            setStatusConfirmVariant(null)
            setPendingStatusOption(null)
        }
        catch {
            showError("Something went wrong, Please try again!")
        } finally {
            setIsStatusChangeSubmitting(false)
        }
    }

    const handleCancelStatusConfirm = () => {
        if (isStatusChangeSubmitting) return
        setStatusConfirmVariant(null)
        setPendingStatusOption(null)
    }

    const handleConfirmStatusChange = (remark?: string) => {
        if (!pendingStatusOption) return
        void changeQuotationStatusApi(pendingStatusOption, remark)
    }

    const handleDropdownIconClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.stopPropagation();
        setIsOpen((prev) => !prev)
    }

    const updateMenuPosition = (flagMatchWidth?: boolean) => {
        if (!anchorRef.current || !menuRef.current) {
            return
        }

        const triggerRect = anchorRef.current.getBoundingClientRect()
        // Set before measuring so the menu starts at the pill's width and lines up with its edges.
        if (flagMatchWidth) {
            menuRef.current.style.minWidth = `${triggerRect.width}px`
        }
        const menuWidth = flagMatchWidth
            ? menuRef.current.offsetWidth
            : Math.max(DROPDOWN_MIN_WIDTH, menuRef.current.offsetWidth)
        const menuHeight = menuRef.current.offsetHeight
        const nextPosition = resolveDropdownPlacement(
            triggerRect,
            menuWidth,
            menuHeight,
        )

        setMenuPosition({
            ...nextPosition,
            isReady: true,
        })
    }

    useLayoutEffect(() => {
        if (!isOpen) {
            setMenuPosition(null)
            return
        }

        setMenuPosition((previous) => ({
            top: previous?.top ?? 0,
            left: previous?.left ?? 0,
            placement: previous?.placement ?? "bottom-left",
            isReady: false,
        }))

        updateMenuPosition(flagMatchAnchorWidth)
    }, [isOpen, flagMatchAnchorWidth])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const handleReposition = () => {
            updateMenuPosition(flagMatchAnchorWidth)
        }

        window.addEventListener("resize", handleReposition)

        return () => {
            window.removeEventListener("resize", handleReposition)
        }
    }, [isOpen, flagMatchAnchorWidth])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node
            const clickedInsideAnchor = Boolean(
                anchorRef.current?.contains(target)
            )
            const clickedInsideMenu = Boolean(
                menuRef.current?.contains(target)
            )

            if (!clickedInsideAnchor && !clickedInsideMenu) {
                setIsOpen(false)
            }
        }

        const isInsideDropdown = (target: EventTarget | null): boolean => {
            return Boolean(
                (anchorRef.current &&
                target instanceof Node &&
                anchorRef.current.contains(target)) ||
                (menuRef.current &&
                target instanceof Node &&
                menuRef.current.contains(target))
            )
        }

        const preventBackgroundScroll = (event: Event) => {
            event.preventDefault()
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false)
                return
            }

            if (
                PAGE_SCROLL_KEYS.has(event.key) &&
                !isInsideDropdown(event.target)
            ) {
                event.preventDefault()
            }
        }

        document.addEventListener("mousedown", handleOutsideClick)
        document.addEventListener("keydown", handleKeyDown)
        document.addEventListener("wheel", preventBackgroundScroll, {
            passive: false,
            capture: true,
        })
        document.addEventListener("touchmove", preventBackgroundScroll, {
            passive: false,
            capture: true,
        })

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick)
            document.removeEventListener("keydown", handleKeyDown)
            document.removeEventListener("wheel", preventBackgroundScroll, true)
            document.removeEventListener(
                "touchmove",
                preventBackgroundScroll,
                true
            )
        }
    }, [isOpen])

    const flagShouldStatusChange = flagViewOnly ? false : getFlagStatusChangeableValue(status_id, status_name);
    const optionsToShow = getStatusOptionsToShow(status_id, quotationStatusOptions);
    const allowAllFollowUpTypes = canShowAllFollowUpTypeOptions(
        status_id,
        status_name,
        quotationStatusOptions,
    );

    const statusMetaView = flagShowListStatusMeta
        ? buildQuotationListStatusMetaView({
            variant,
            confirmedOn: listStatusMeta?.confirmedOn,
            nextFollowUpDate: listStatusMeta?.nextFollowUpDate,
            lostReason: listStatusMeta?.lostReason,
        })
        : null

    const statusMetaClassName = statusMetaView
        ? {
            confirmed: styles.statusMetaConfirmed,
            followUp: styles.statusMetaFollowUp,
            followUpDueToday: styles.statusMetaFollowUpDueToday,
            lostReason: styles.statusMetaLostReason,
        }[statusMetaView.lineClassName]
        : ""

    const updateLostReasonPopoverPosition = () => {
        const trigger = lostReasonInfoRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const popoverWidth = lostReasonPopoverRef.current?.offsetWidth || 220
        const left = Math.min(
            Math.max(8, rect.left + rect.width / 2 - popoverWidth / 2),
            window.innerWidth - popoverWidth - 8,
        )

        setLostReasonPopoverPosition({
            top: rect.bottom + 8,
            left,
        })
    }

    useLayoutEffect(() => {
        if (!isLostReasonPopoverOpen && !isHoveringLostReason) {
            setLostReasonPopoverPosition(null)
            return
        }

        updateLostReasonPopoverPosition()
    }, [isLostReasonPopoverOpen, isHoveringLostReason, statusMetaView?.lostReasonText])

    useEffect(() => {
        if (!isLostReasonPopoverOpen) return

        const handleReposition = () => updateLostReasonPopoverPosition()
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                lostReasonInfoRef.current?.contains(target)
                || lostReasonPopoverRef.current?.contains(target)
            ) {
                return
            }
            setIsLostReasonPopoverOpen(false)
        }

        window.addEventListener("resize", handleReposition)
        window.addEventListener("scroll", handleReposition, true)
        document.addEventListener("mousedown", handleOutsideClick)

        return () => {
            window.removeEventListener("resize", handleReposition)
            window.removeEventListener("scroll", handleReposition, true)
            document.removeEventListener("mousedown", handleOutsideClick)
        }
    }, [isLostReasonPopoverOpen])

    const handleLostReasonInfoClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setIsLostReasonPopoverOpen((prev) => !prev)
    }

    const handleLostReasonMouseEnter = () => setIsHoveringLostReason(true)
    const handleLostReasonMouseLeave = () => setIsHoveringLostReason(false)

    const handleActivityHistoryIconClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.stopPropagation();
        if (onActivityHistoryOpen) {
            onActivityHistoryOpen()
            return
        }
        setFlagShowActivityHistoryPopup(true);
    }

    const handleCloseActivityHistoryPopup = () => {
        setFlagShowActivityHistoryPopup(false);
    }

    const useExternalActivityHistory = Boolean(onActivityHistoryOpen)
    const showLostReasonPopover = isLostReasonPopoverOpen || isHoveringLostReason

    return (
        <Fragment>
            <MarkQuotationStatusConfirmPopup
                isOpen={Boolean(statusConfirmVariant)}
                variant={statusConfirmVariant || "confirmed"}
                staCode={String(sta_code || "").trim()}
                isSubmitting={isStatusChangeSubmitting}
                onCancel={handleCancelStatusConfirm}
                onConfirm={handleConfirmStatusChange}
            />

            {!useExternalActivityHistory && (
                <ActivityHistoryPopup
                    isOpen={flagShowActivityHistoryPopup}
                    quotationId={quotation_id || ""}
                    staCode={sta_code || ""}
                    startedAt={started_at}
                    createdAt={created_at}
                    quotationDownloads={quotation_downloads}
                    followUps={follow_ups}
                    followUpTypes={quotationFollowUpTypeOptions}
                    allowAllFollowUpTypes={allowAllFollowUpTypes}
                    onClose={handleCloseActivityHistoryPopup}
                    onFollowUpsChange={onFollowUpsChange}
                />
            )}

            <div className={flagShowListStatusMeta ? styles.statusCell : styles.pillWrapper}>
                <div
                    className={flagShowListStatusMeta ? styles.pillRow : styles.pillWrapper}
                    ref={anchorRef}
                >
                    <span
                        className={[
                            styles.pill,
                            variantClass[variant],
                            className,
                        ]
                            .filter(Boolean)
                            .join(" ")}
                        onClick={flagShouldStatusChange ? handleDropdownIconClick : () => {}}
                    >
                        {flagShowStatusLabel && "Status: "}{status_name}
                        {flagShouldStatusChange && (
                            <div className={styles.dropdownWrapper}>
                                <button
                                    type="button"
                                    className={styles.dropdownButton}
                                    onClick={handleDropdownIconClick}
                                    aria-label="Change quotation status"
                                    aria-expanded={isOpen}
                                >
                                    <IconsColor
                                        iconName={"chevronDown"}
                                        width={"15px"}
                                        height={"15px"}
                                    />
                                </button>
                            </div>
                        )}
                    </span>
                    {statusMetaView?.showLostReasonInfo && (
                        <div
                            className={styles.lostReasonInfoWrapper}
                            onMouseEnter={handleLostReasonMouseEnter}
                            onMouseLeave={handleLostReasonMouseLeave}
                        >
                            <button
                                ref={lostReasonInfoRef}
                                type="button"
                                className={styles.lostReasonInfoBtn}
                                onClick={handleLostReasonInfoClick}
                                aria-label="View reason for loss"
                                aria-expanded={showLostReasonPopover}
                            >
                                <IoMdInformationCircleOutline size={22} color="#991B1B" />
                            </button>
                        </div>
                    )}
                    {flagShowActivityHistory && (
                        <button
                            type="button"
                            className={styles.activityHistoryBtn}
                            onClick={handleActivityHistoryIconClick}
                            aria-label="View activity history"
                        >
                            <LuFileClock width={26} height={26} color={"#64748B"} strokeWidth={2.5} />
                        </button>
                    )}


                </div>

                {statusMetaView && (
                    <div className={[styles.statusMetaLine, statusMetaClassName].join(" ")}>
                        {statusMetaView.lineText}
                    </div>
                )}
            </div>

            {(isLostReasonPopoverOpen || isHoveringLostReason) && statusMetaView?.showLostReasonInfo && createPortal(
                <div
                    ref={lostReasonPopoverRef}
                    className={styles.lostReasonPopover}
                    style={{
                        top: lostReasonPopoverPosition?.top ?? 0,
                        left: lostReasonPopoverPosition?.left ?? 0,
                        visibility: lostReasonPopoverPosition ? "visible" : "hidden",
                    }}
                    role="dialog"
                    aria-label="Reason for loss"
                    onClick={(event: React.MouseEvent<HTMLDivElement>): void => event.stopPropagation()}
                    onMouseEnter={handleLostReasonMouseEnter}
                    onMouseLeave={handleLostReasonMouseLeave}
                >
                    <p className={styles.lostReasonPopoverTitle}>Reason for loss</p>
                    <p className={styles.lostReasonPopoverText}>
                        {statusMetaView.lostReasonText}
                    </p>
                </div>,
                document.body,
            )}

            {isOpen && flagShouldStatusChange && createPortal(
                <div
                    ref={menuRef}
                    className={[
                        styles.dropdownMenu,
                        styles.dropdownMenuPortal,
                    ].join(" ")}
                    style={{
                        top: menuPosition?.top ?? 0,
                        left: (menuPosition?.left ?? 0),
                        visibility: menuPosition?.isReady ? "visible" : "hidden",
                    }}
                    role="listbox"
                    aria-label="Quotation status options"
                >
                    {optionsToShow.map((option) => (
                        <button
                            key={option.quotation_status_id}
                            type="button"
                            className={[styles.dropdownOption, dropdownOptionClassName].filter(Boolean).join(" ")}
                            onClick={(event) => handleOptionClick(event, option)}
                            role="option"
                            aria-selected={status_id === option.quotation_status_id}
                        >
                            {option.quotation_status_name}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </Fragment>
    )
}

export default QuotationStatusPill